import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Loader2, Home, LogIn, Eye, EyeOff } from 'lucide-react';
import { useAlert } from '../components/ui/alert';

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showWelcomeContent, setShowWelcomeContent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showError } = useAlert();

  useEffect(() => {
    setTimeout(() => {
      setIsPageLoaded(true);
      setTimeout(() => {
        setShowWelcomeContent(true);
        setTimeout(() => {
          setShowForm(true);
        }, 300);
      }, 500);
    }, 100);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNavigateWithAnimation = (path) => {
    // 1. Prima nascondiamo il form (scivola verso destra)
    setShowForm(false);
    
    // 2. Nascondiamo il contenuto della barra
    setTimeout(() => {
      setShowWelcomeContent(false);
      
      // 3. Dopo che il contenuto è scomparso, nascondiamo la barra laterale
      setTimeout(() => {
        setIsPageLoaded(false);
        
        // 4. Aspettiamo che l'animazione della barra sia completata
        setTimeout(() => {
          navigate(path);
        }, 500); // Tempo per l'animazione scale-y della barra
      }, 300); // Tempo per l'animazione del contenuto
    }, 500); // Tempo per l'animazione translate-x del form
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      showError('Username e password sono obbligatori');
      return;
    }

    setIsLoading(true);

    try {
      await login(formData.username, formData.password, true); // true per usare MySQL
      handleNavigateWithAnimation('/dashboard');
    } catch (error) {
      showError(error.response?.data?.error || 'Login fallito');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-light-surface dark:bg-dark-surface flex">
      {/* Left side - Form */}
      <div className={`
        w-full lg:w-1/2 flex items-center justify-center p-8
        transition-all duration-500 ease-out
        transform origin-right
        ${showForm ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'}
      `}>
        <div className="w-full max-w-md">
          <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-xl shadow-surface p-8">
            <div className="flex items-center justify-center gap-3 mb-8">
              <LogIn className="w-8 h-8 text-light-secondary dark:text-dark-secondary" />
              <h1 className="text-3xl font-bold text-light-on-surface dark:text-dark-on-surface">
                Accesso
              </h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-light-on-surface/50 dark:text-dark-on-surface/50 w-5 h-5" />
                <input
                  type="text"
                  name="username"
                  placeholder="Username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="w-full p-3 pl-12 bg-light-surface-container-low dark:bg-dark-surface-container-low rounded-lg border border-light-outline dark:border-dark-outline focus:border-light-secondary dark:focus:border-dark-secondary outline-none text-light-on-surface dark:text-dark-on-surface"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-light-on-surface/50 dark:text-dark-on-surface/50 w-5 h-5" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full p-3 pl-12 pr-12 bg-light-surface-container-low dark:bg-dark-surface-container-low rounded-lg border border-light-outline dark:border-dark-outline focus:border-light-secondary dark:focus:border-dark-secondary outline-none text-light-on-surface dark:text-dark-on-surface"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-light-on-surface/50 dark:text-dark-on-surface/50 hover:text-light-on-surface dark:hover:text-dark-on-surface transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-light-secondary dark:bg-dark-secondary text-light-on-secondary dark:text-dark-on-secondary rounded-lg hover:bg-light-secondary-container dark:hover:bg-dark-secondary-container transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Accesso...</span>
                  </>
                ) : (
                  <span>Accedi</span>
                )}
              </button>
            </form>

            <div className="mt-4">
              <button
                onClick={() => handleNavigateWithAnimation('/')}
                className="w-full py-3 px-4 bg-transparent border border-light-outline dark:border-dark-outline text-light-on-surface dark:text-dark-on-surface rounded-lg hover:bg-light-surface-container-low dark:hover:bg-dark-surface-container-low transition-colors flex items-center justify-center gap-2"
              >
                <Home className="w-5 h-5" />
                <span>Torna alla Home</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Welcome */}
      <div className={`
        hidden lg:flex lg:w-1/2 bg-light-surface-container dark:bg-dark-surface-container 
        items-center justify-center p-8
        transition-all duration-500 ease-out
        overflow-hidden
        transform origin-center
        ${isPageLoaded ? 'scale-y-100' : 'scale-y-0'}
      `}>
        <div className={`
          max-w-md text-center
          transition-all duration-300 ease-out
          ${showWelcomeContent ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}
        `}>
          <User className="w-24 h-24 mx-auto mb-6 text-light-secondary dark:text-dark-secondary" />
          <h2 className="text-4xl font-bold text-light-on-surface dark:text-dark-on-surface mb-4">
            Benvenuto in myBecco
          </h2>
          <p className="text-light-on-surface/70 dark:text-dark-on-surface/70 text-lg">
            Accedi alla tua area riservata.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;